import { helpWhy } from '../../help/why';
import React, { useEffect, useState } from 'react';
import {
    Box, Button, CircularProgress, FormControlLabel, MenuItem, Paper, Switch, TextField, Typography,
} from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { CommercialSettingsService } from '../../services/commercialSettings.service';
import { AccountService } from '../../services/account.service';
import { Account } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { leafAccounts } from '../../utils/postingAccounts';
import { PERMISSIONS } from '../../constants/permissions';
import { CommissionDirection, IUpdateCommercialSettingsRequest } from '../../interfaces/commercial.interface';
import { helpElement } from '../../help/attrs';

const EMPTY: IUpdateCommercialSettingsRequest = {
    defaultCurrencyCode: 'USD', repatriationWindowDays: 120, lcExpiryWarningDays: 30,
    shipmentDateWarningDays: 15, udValidityWarningDays: 30, defaultB2bEntitlementPercent: 70,
    cashIncentiveEnabled: false, defaultCashIncentiveRatePercent: 0,
    exportProceedsAccountId: '', bankAccountId: '', bankChargesAccountId: '', exporterBankDetails: '',
    exportReceivableAccountId: '', fxGainAccountId: '', fxLossAccountId: '',
    cashIncentiveReceivableAccountId: '', cashIncentiveIncomeAccountId: '',
    unrealizedFxGainAccountId: '', unrealizedFxLossAccountId: '',
    b2bAcceptanceLiabilityAccountId: '', b2bMarginAccountId: '', b2bClearingAccountId: '', sourceTaxAccountId: '', cashIncentiveBankAccountId: '',
    commissionDirection: 'PAYABLE',
};

type AccountKey = 'exportProceedsAccountId' | 'bankAccountId' | 'bankChargesAccountId' | 'exportReceivableAccountId'
    | 'fxGainAccountId' | 'fxLossAccountId' | 'cashIncentiveReceivableAccountId' | 'cashIncentiveIncomeAccountId'
    | 'unrealizedFxGainAccountId' | 'unrealizedFxLossAccountId'
    | 'b2bAcceptanceLiabilityAccountId' | 'b2bMarginAccountId' | 'b2bClearingAccountId' | 'sourceTaxAccountId' | 'cashIncentiveBankAccountId';

/** Pickers for the ledger postings: revenue/receivable at shipment, FX on realisation, cash incentive. */
const POSTING_ACCOUNT_FIELDS: { key: AccountKey; label: string; hint?: string; helpKey: string }[] = [
    { key: 'exportReceivableAccountId', helpKey: 'commercial.settings.field.exportReceivableAccount', label: 'Export Receivable Account', hint: 'Debited at shipment (EXP form) at the booking rate; credited when proceeds are realised.' },
    { key: 'exportProceedsAccountId', helpKey: 'commercial.settings.field.exportProceedsAccount', label: 'Export Sales / Proceeds Account', hint: 'Revenue: credited at shipment. Legacy EXPs with no booked receivable still credit it at realisation.' },
    { key: 'bankAccountId', helpKey: 'commercial.settings.field.bankAccount', label: 'Bank Account', hint: 'Export proceeds are realised into it. A USD bank account takes USD realisations only.' },
    { key: 'bankChargesAccountId', helpKey: 'commercial.settings.field.bankChargesAccount', label: 'Bank Charges Account' },
    { key: 'fxGainAccountId', helpKey: 'commercial.settings.field.fxGainAccount', label: 'FX Gain Account', hint: 'Realised gain: proceeds realised above the booked rate.' },
    { key: 'fxLossAccountId', helpKey: 'commercial.settings.field.fxLossAccount', label: 'FX Loss Account', hint: 'Realised loss: proceeds realised below the booked rate.' },
    { key: 'unrealizedFxGainAccountId', helpKey: 'commercial.settings.field.unrealizedFxGainAccount', label: 'Unrealised FX Gain Account', hint: 'Revenue account (BD chart 4004020000). Period-end revaluation of open foreign-currency receivables credits it; the entry auto-reverses next day.' },
    { key: 'unrealizedFxLossAccountId', helpKey: 'commercial.settings.field.unrealizedFxLossAccount', label: 'Unrealised FX Loss Account', hint: 'Expense account (BD chart 5007020000). Debited when open foreign-currency receivables lose value at the closing rate.' },
    { key: 'cashIncentiveReceivableAccountId', helpKey: 'commercial.settings.field.cashIncentiveReceivableAccount', label: 'Cash Incentive Receivable Account', hint: 'Debited when a claim is approved; cleared when it is received.' },
    { key: 'cashIncentiveIncomeAccountId', helpKey: 'commercial.settings.field.cashIncentiveIncomeAccount', label: 'Cash Incentive Income Account', hint: 'Credited when a claim is approved; also takes any claimed-vs-received difference.' },
    { key: 'cashIncentiveBankAccountId', helpKey: 'commercial.settings.field.cashIncentiveBankAccount', label: 'Cash Incentive Bank Account', hint: 'BDT bank account debited when a cash incentive is received. Required when the Bank Account above is a foreign-currency account; empty uses the Bank Account.' },
    { key: 'sourceTaxAccountId', helpKey: 'commercial.settings.field.sourceTaxAccount', label: 'Source Tax (AIT) Account', hint: 'Asset (advance income tax). Debited for tax deducted at source on export proceeds and cash incentive receipts.' },
];

/** Back-to-back LC postings (accounting wave 2): margin, bank acceptance liability, goods clearing. */
const B2B_ACCOUNT_FIELDS: { key: AccountKey; label: string; hint?: string; helpKey: string }[] = [
    { key: 'b2bAcceptanceLiabilityAccountId', helpKey: 'commercial.settings.field.b2bAcceptanceLiabilityAccount', label: 'B2B Acceptance Liability Account', hint: 'Liability (bank). Credited when the bank accepts import documents; debited at the maturity payment.' },
    { key: 'b2bMarginAccountId', helpKey: 'commercial.settings.field.b2bMarginAccount', label: 'B2B LC Margin Account', hint: 'Asset. Debited when the bank takes LC margin; credited when it is released.' },
    { key: 'b2bClearingAccountId', helpKey: 'commercial.settings.field.b2bClearingAccount', label: 'B2B Clearing Account', hint: 'Debited at acceptance. Leave empty to use the procurement GRNI account (goods received not invoiced).' },
];

const WINDOW_FIELDS: { key: keyof IUpdateCommercialSettingsRequest; label: string; helpKey: string }[] = [
    { key: 'repatriationWindowDays', helpKey: 'commercial.settings.field.repatriationWindow', label: 'Repatriation window (days)' },
    { key: 'lcExpiryWarningDays', helpKey: 'commercial.settings.field.lcExpiryWarning', label: 'LC expiry warning (days)' },
    { key: 'shipmentDateWarningDays', helpKey: 'commercial.settings.field.shipmentDateWarning', label: 'Shipment date warning (days)' },
    { key: 'udValidityWarningDays', helpKey: 'commercial.settings.field.udValidityWarning', label: 'UD validity warning (days)' },
];

const MIN_WINDOW_DAYS = 1;
const MAX_WINDOW_DAYS = 3650;

/**
 * All eight `commercial_settings` columns (§5.2). The four warning-window fields range-validate
 * client-side against R-21's documented 1..3650 bounds -- matching the backend's
 * `@Min(1) @Max(3650)` on `UpdateCommercialSettingsRequest` -- and the Save button stays disabled,
 * with an inline error, until every window is back in range. The incentive-screens visibility
 * elsewhere in the module reads `cashIncentiveEnabled` from this same settings object (R-3).
 */
const CommercialSettingsPage: React.FC = () => {
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.COMMERCIAL_SETTINGS_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;
    const [form, setForm] = useState<IUpdateCommercialSettingsRequest>(EMPTY);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            const [settings, accountsRes] = await Promise.all([
                CommercialSettingsService.get(institutionId),
                // The account pickers only matter to someone who can edit; a view-only user has no
                // chart-of-accounts access, so don't ask (it would just 403 and toast).
                canManage ? AccountService.getAllAccounts() : Promise.resolve([] as Account[]),
            ]);
            setAccounts(accountsRes ?? []);
            setForm({
                defaultCurrencyCode: settings.defaultCurrencyCode,
                repatriationWindowDays: settings.repatriationWindowDays,
                lcExpiryWarningDays: settings.lcExpiryWarningDays,
                shipmentDateWarningDays: settings.shipmentDateWarningDays,
                udValidityWarningDays: settings.udValidityWarningDays,
                defaultB2bEntitlementPercent: settings.defaultB2bEntitlementPercent,
                cashIncentiveEnabled: settings.cashIncentiveEnabled,
                defaultCashIncentiveRatePercent: settings.defaultCashIncentiveRatePercent,
                exportProceedsAccountId: settings.exportProceedsAccountId ?? '',
                bankAccountId: settings.bankAccountId ?? '',
                bankChargesAccountId: settings.bankChargesAccountId ?? '',
                exporterBankDetails: settings.exporterBankDetails ?? '',
                exportReceivableAccountId: settings.exportReceivableAccountId ?? '',
                fxGainAccountId: settings.fxGainAccountId ?? '',
                fxLossAccountId: settings.fxLossAccountId ?? '',
                cashIncentiveReceivableAccountId: settings.cashIncentiveReceivableAccountId ?? '',
                cashIncentiveIncomeAccountId: settings.cashIncentiveIncomeAccountId ?? '',
                unrealizedFxGainAccountId: settings.unrealizedFxGainAccountId ?? '',
                unrealizedFxLossAccountId: settings.unrealizedFxLossAccountId ?? '',
                b2bAcceptanceLiabilityAccountId: settings.b2bAcceptanceLiabilityAccountId ?? '',
                b2bMarginAccountId: settings.b2bMarginAccountId ?? '',
                b2bClearingAccountId: settings.b2bClearingAccountId ?? '',
                sourceTaxAccountId: settings.sourceTaxAccountId ?? '',
                cashIncentiveBankAccountId: settings.cashIncentiveBankAccountId ?? '',
                commissionDirection: settings.commissionDirection ?? 'PAYABLE',
            });
        } catch (e) {
            toast.showApiError(e, 'Failed to load commercial settings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

    const setAccountField = (k: AccountKey) =>
        (id: string) => setForm({ ...form, [k]: id });

    const windowOutOfRange = (value: number) =>
        !Number.isFinite(value) || value < MIN_WINDOW_DAYS || value > MAX_WINDOW_DAYS;

    const anyWindowInvalid = WINDOW_FIELDS.some(f => windowOutOfRange(Number(form[f.key])));
    const currencyValid = /^[A-Z]{3}$/.test(form.defaultCurrencyCode);
    const percentValid = form.defaultB2bEntitlementPercent >= 0 && form.defaultB2bEntitlementPercent <= 100
        && form.defaultCashIncentiveRatePercent >= 0 && form.defaultCashIncentiveRatePercent <= 100;
    const valid = currencyValid && percentValid && !anyWindowInvalid;

    const saveWhy = saving ? null
        : !currencyValid ? 'Enter a 3-letter capital currency code, for example BDT.'
        : !percentValid ? 'Keep the default percentages between 0 and 100.'
        : anyWindowInvalid ? 'Keep the warning days between 1 and 3650.'
        : null;

    const handleSave = async () => {
        if (!valid) return;
        try {
            setSaving(true);
            await CommercialSettingsService.upsert(institutionId, {
                ...form,
                exportProceedsAccountId: form.exportProceedsAccountId || null,
                bankAccountId: form.bankAccountId || null,
                bankChargesAccountId: form.bankChargesAccountId || null,
                exporterBankDetails: form.exporterBankDetails?.trim() || null,
                exportReceivableAccountId: form.exportReceivableAccountId || null,
                fxGainAccountId: form.fxGainAccountId || null,
                fxLossAccountId: form.fxLossAccountId || null,
                cashIncentiveReceivableAccountId: form.cashIncentiveReceivableAccountId || null,
                cashIncentiveIncomeAccountId: form.cashIncentiveIncomeAccountId || null,
                unrealizedFxGainAccountId: form.unrealizedFxGainAccountId || null,
                unrealizedFxLossAccountId: form.unrealizedFxLossAccountId || null,
                b2bAcceptanceLiabilityAccountId: form.b2bAcceptanceLiabilityAccountId || null,
                b2bMarginAccountId: form.b2bMarginAccountId || null,
                b2bClearingAccountId: form.b2bClearingAccountId || null,
                sourceTaxAccountId: form.sourceTaxAccountId || null,
                cashIncentiveBankAccountId: form.cashIncentiveBankAccountId || null,
            });
            toast.showSuccess('Commercial settings saved');
        } catch (e) {
            toast.showApiError(e, 'Failed to save commercial settings');
        } finally {
            setSaving(false);
        }
    };

    // Group (header) accounts never receive postings, so only leaf accounts are offered.
    const postingAccounts = leafAccounts(accounts);

    if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ p: 3, maxWidth: 720 }} data-help-page="commercial.settings.page">
            <Typography variant="h5" sx={{ mb: 2 }}>Commercial Settings</Typography>
            <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }} data-help-section="commercial.settings.section.defaults">
                <TextField data-help="commercial.settings.field.defaultCurrency" label="Default Currency Code" required value={form.defaultCurrencyCode}
                    disabled={!canManage}
                    error={!currencyValid}
                    helperText={!currencyValid ? 'Must be a 3-letter uppercase ISO currency code' : undefined}
                    onChange={e => setForm({ ...form, defaultCurrencyCode: e.target.value.toUpperCase() })} />

                {WINDOW_FIELDS.map(f => {
                    const value = Number(form[f.key]);
                    const invalid = windowOutOfRange(value);
                    return (
                        <TextField
                            key={f.key}
                            {...helpElement(f.helpKey)}
                            label={f.label}
                            type="number"
                            required
                            disabled={!canManage}
                            value={form[f.key] as number}
                            error={invalid}
                            helperText={invalid ? `Must be between ${MIN_WINDOW_DAYS} and ${MAX_WINDOW_DAYS} days` : undefined}
                            onChange={e => setForm({ ...form, [f.key]: Number(e.target.value) })}
                        />
                    );
                })}

                <TextField data-help="commercial.settings.field.defaultB2bPercent" label="Default B2B Entitlement Percent" type="number" required
                    disabled={!canManage}
                    value={form.defaultB2bEntitlementPercent}
                    error={form.defaultB2bEntitlementPercent < 0 || form.defaultB2bEntitlementPercent > 100}
                    helperText="Between 0 and 100"
                    onChange={e => setForm({ ...form, defaultB2bEntitlementPercent: Number(e.target.value) })} />

                <FormControlLabel
                    data-help="commercial.settings.field.cashIncentiveEnabled"
                    control={<Switch checked={form.cashIncentiveEnabled} disabled={!canManage}
                        onChange={e => setForm({ ...form, cashIncentiveEnabled: e.target.checked })} />}
                    label="Cash incentive claims enabled"
                />
                <Typography variant="caption" color="text.secondary">
                    Off by default. While it is off, the cash-incentive screens show a notice instead of the claims register.
                </Typography>

                <TextField data-help="commercial.settings.field.commissionDirection" select label="Agent commission direction"
                    disabled={!canManage}
                    value={form.commissionDirection ?? 'PAYABLE'}
                    helperText="Payable: you pay the buying agent. Receivable: you earn it as a buying house. Only labels the commission register; nothing is posted."
                    onChange={e => setForm({ ...form, commissionDirection: e.target.value as CommissionDirection })}>
                    <MenuItem value="PAYABLE">Payable to the agent</MenuItem>
                    <MenuItem value="RECEIVABLE">Receivable from the agent</MenuItem>
                </TextField>

                <TextField data-help="commercial.settings.field.defaultIncentiveRate" label="Default Cash Incentive Rate Percent" type="number" required
                    disabled={!canManage}
                    value={form.defaultCashIncentiveRatePercent}
                    error={form.defaultCashIncentiveRatePercent < 0 || form.defaultCashIncentiveRatePercent > 100}
                    helperText="Between 0 and 100"
                    onChange={e => setForm({ ...form, defaultCashIncentiveRatePercent: Number(e.target.value) })} />

                <Typography variant="subtitle2" sx={{ mt: 1 }}>Ledger accounts for export postings</Typography>
                <Typography variant="caption" color="text.secondary">
                    Export revenue and the receivable are booked when an EXP form is created (shipment); realisation
                    clears the receivable and books the realised FX gain or loss; cash-incentive claims post when
                    approved and when received. A posting whose account is not set here is skipped and the screen
                    shows a warning.
                </Typography>

                {POSTING_ACCOUNT_FIELDS.map(f => (
                    <Box key={f.key} data-testid={`posting-account-${f.key}`}>
                        <AccountAutocomplete
                            {...helpElement(f.helpKey)}
                            label={f.label}
                            accounts={postingAccounts}
                            value={form[f.key] ?? ''}
                            onChange={setAccountField(f.key)}
                            disabled={!canManage}
                            placeholder="None"
                        />
                        {f.hint && <Typography variant="caption" color="text.secondary">{f.hint}</Typography>}
                    </Box>
                ))}

                <Typography variant="subtitle2" sx={{ mt: 1 }}>Ledger accounts for back-to-back LC postings</Typography>
                <Typography variant="caption" color="text.secondary">
                    LC margin, bank acceptance of import documents and the maturity payment post to these accounts.
                    A posting whose account is not set here is refused with a message naming the missing account.
                </Typography>
                <Typography variant="caption" color="text.secondary" data-testid="b2b-ap-hint">
                    B2B imports: don't book a supplier bill. The bank acceptance already records the liability, so a supplier bill for the same import would double-count it.
                </Typography>

                {B2B_ACCOUNT_FIELDS.map(f => (
                    <Box key={f.key} data-testid={`posting-account-${f.key}`}>
                        <AccountAutocomplete
                            {...helpElement(f.helpKey)}
                            label={f.label}
                            accounts={postingAccounts}
                            value={form[f.key] ?? ''}
                            onChange={setAccountField(f.key)}
                            disabled={!canManage}
                            placeholder="None"
                        />
                        {f.hint && <Typography variant="caption" color="text.secondary">{f.hint}</Typography>}
                    </Box>
                ))}

                <Typography variant="subtitle2" sx={{ mt: 1 }}>Export invoice details</Typography>

                <TextField
                    data-help="commercial.settings.field.exporterBankDetails"
                    label="Exporter bank details (printed on export invoices)"
                    multiline
                    rows={4}
                    disabled={!canManage}
                    value={form.exporterBankDetails ?? ''}
                    helperText="Optional -- falls back to invoice branding's bank block when left empty."
                    onChange={e => setForm({ ...form, exporterBankDetails: e.target.value })}
                />

                {canManage && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button data-help="commercial.settings.save" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} variant="contained" disabled={saving || !valid} onClick={handleSave}>Save</Button>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default CommercialSettingsPage;
